import React, { useEffect, useMemo } from 'react';

import { RequiredSetlistGenerateSettings } from '@/components/Layout/components/GenerateSetlist';
import { TeamMilestones, UserMilestones } from '@/config/constants';
import { Permissions, useIsChargebeeCouponUsableQuery } from '@/graphql';
import { getMarketingCoupon, getMarketingPlan } from '@/helpers/pricing';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { useFireAnalyticsPageView } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { usePermissions } from '@/providers/PermissionsProvider/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import {
	openCheckout,
	useSubscription,
} from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import { IonPage } from '@ionic/react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router';
import * as Yup from 'yup';

import styles from './Onboarding.module.scss';
import StepFive from './StepFive';
import StepFour from './StepFour';
import StepInfographic from './StepInfographic/StepInfographic';
import StepOne from './StepOne';
import StepSeven from './StepSeven';
import StepSix from './StepSix';
import StepThree from './StepThree';
import StepTwo from './StepTwo';

const signUpValidationSchema = Yup.object().shape({
	displayName: Yup.string().required('Please enter a name'),
});

type OnboardingGenerateSettings = Pick<
	RequiredSetlistGenerateSettings,
	'totalSongPositions' | 'postSermonSongPosition' | 'bibleReference'
> & {
	initialSongIds: string[];
};

export const OnboardingContext = React.createContext<{
	step: number;
	canUpdateTeam: boolean;
	/** True when the user should see the add-service step (can update team and team has no services yet). */
	shouldAddService: boolean;
	generateSettings: OnboardingGenerateSettings;
	/** Logical indicator indices (0-based) that have associated data — used to render dots as light blue and allow navigation. */
	completedStepIndices: number[];
	setStep: (step: number) => void;
	setGenerateSettings: React.Dispatch<
		React.SetStateAction<OnboardingGenerateSettings>
	>;
}>({
	step: 0,
	canUpdateTeam: false,
	shouldAddService: false,
	completedStepIndices: [],
	generateSettings: {
		totalSongPositions: 5,
		postSermonSongPosition: 1,
		bibleReference: undefined,
		initialSongIds: [],
	},
	setStep: () => {},
	setGenerateSettings: () => {},
});

const Onboarding = () => {
	const { user, hasMilestones } = useAuth();
	const { hasPermission } = usePermissions();
	const { services, isLoading: isLoadingServices } = useServices();
	const { team, hasMilestones: teamHasMilestones } = useTeam();
	// const { data, isLoading } = useGetDashboardOnboardingQuery();
	const navigate = useNavigate();
	const { chargebeeInstance, cart } = useSubscription();
	const { integrations } = useEnvironment();
	const [title] = useFormattedTitle('Asaph | Onboarding');

	const [step, setStep] = React.useState(
		hasMilestones([UserMilestones.PROFILE_SET_UP]) ? 8 : 1,
	);
	const [hasServiceInitially, setHasServiceInitially] = React.useState(false);
	const [generateSettings, setGenerateSettings] =
		React.useState<OnboardingGenerateSettings>({
			totalSongPositions: 5,
			postSermonSongPosition: 1,
			bibleReference: undefined,
			initialSongIds: [],
		});
	const completedStepIndices = useMemo(() => {
		const indices: number[] = [];
		if (user.displayName?.trim()) indices.push(0);
		if (user.role) indices.push(1);
		if (team?.name?.trim()) indices.push(2);
		if (services.length > 0) indices.push(3);
		if (teamHasMilestones([TeamMilestones.ADDED_INITIAL_SONGS])) indices.push(4);
		return indices;
	}, [user, team, services, teamHasMilestones]);

	useEffect(() => {
		if (!isLoadingServices) {
			setHasServiceInitially(services.length > 0);
		}
		// We need to check if the team has a service initially so we can determine
		// if the user should see the add-service step. We don't want this to be re-run
		// when a new service is added.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingServices]);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const { search } = useLocation();
	const checkoutFired = React.useRef(false);
	const requestedCouponId = getMarketingCoupon(search);
	// An unknown coupon makes Chargebee replace the whole checkout with an
	// error page, so only pass it on once the server confirms it is usable.
	const couponCheck = useIsChargebeeCouponUsableQuery(
		{ couponId: requestedCouponId ?? '' },
		{ enabled: !!requestedCouponId, retry: false, refetchOnWindowFocus: false },
	);
	const couponSettled = !requestedCouponId || !couponCheck.isLoading;
	const couponId =
		requestedCouponId && couponCheck.data?.isChargebeeCouponUsable
			? requestedCouponId
			: undefined;

	useEffect(() => {
		const plan = getMarketingPlan(search);
		// New accounts start with an empty team id until the team query resolves,
		// so wait for it; openCheckout throws without one.
		if (
			plan === 'leader' &&
			'initializeProduct' in chargebeeInstance &&
			team.id &&
			couponSettled &&
			!checkoutFired.current
		) {
			checkoutFired.current = true;
			openCheckout({
				cbInstance: chargebeeInstance,
				cart,
				user,
				team,
				period: 'Monthly',
				currency: 'USD',
				planPriceIdPrefix: integrations.chargebee.planId,
				couponId,
			});
		}
		// TODO: wire Team checkout once Chargebee plan is configured
	}, [cart, chargebeeInstance, couponId, couponSettled, integrations.chargebee.planId, navigate, search, team, user]);

	return (
		<OnboardingContext.Provider
			value={{
				step,
				canUpdateTeam: hasPermission(Permissions.TeamUpdate),
				shouldAddService:
					!hasServiceInitially &&
					hasPermission(Permissions.ServiceCreate),
				completedStepIndices,
				setStep,
				generateSettings,
				setGenerateSettings,
			}}
		>
			<IonPage className={styles['page']}>
				<Helmet title={title} />
				<RenderStep />
			</IonPage>
		</OnboardingContext.Provider>
	);
};

export default Onboarding;

const RenderStep = () => {
	const { step, canUpdateTeam } = React.useContext(OnboardingContext);

	switch (step) {
		case 2:
			return <StepTwo />;
		case 3:
			return <StepThree />;
		case 4:
			return <StepFour />;
		case 5:
			return canUpdateTeam ? <StepFive /> : <StepOne />;
		case 6:
			return canUpdateTeam ? <StepSix /> : <StepOne />;
		case 7:
			return canUpdateTeam ? <StepSeven /> : <StepOne />;
		case 8:
		case 9:
		case 10:
		case 11:
		case 12:
			return <StepInfographic />;
		default:
			return <StepOne />; // fallback to step one
	}
};
